import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useDataBus } from "@/lib/dataBus";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ExternalLink, FolderOpen, Trash2, Plus, GripVertical, Settings2, Sparkles, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import EditProjectDialog, { PROJECT_STATUSES } from "@/components/EditProjectDialog";
import { TableRowsSkeleton } from "@/components/LoadingSkeleton";

import {
  isUnassigned,
  InlineAssignees,
  InlineStatusPill,
  InlineStatusNote,
} from "@/components/projects/InlineCells";
import {
  AiReadyBanner,
  UnassignedBanner,
  MissingWriterBanner,
  MissingPublisherBanner,
  KwUnassignedBanner,
  NewChangesBanner,
} from "@/components/projects/ProjectAlerts";
import ProjectFilters from "@/components/projects/ProjectFilters";
import PushToAIDialog from "@/components/projects/PushToAIDialog";
import TeamHierarchyCard from "@/components/projects/TeamHierarchyCard";

export default function ProjectsList() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(false);
  const [showUnassignedDropdown, setShowUnassignedDropdown] = useState(false);
  const [showMissingWriterDropdown, setShowMissingWriterDropdown] = useState(false);
  const [showMissingPublisherDropdown, setShowMissingPublisherDropdown] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // People filters — pick one user per role to narrow the list. `"any"`
  // means "no filter" for that role.
  const [cmFilter, setCmFilter] = useState("any");
  const [tlFilter, setTlFilter] = useState("any");
  const [writerFilter, setWriterFilter] = useState("any");
  const [editorFilter, setEditorFilter] = useState("any");
  const [publisherFilter, setPublisherFilter] = useState("any");

  // Iter 44 — project-activity notifications. Auto-refreshed by the existing
  // 20s poll below and by the dataBus mutation stream.
  const [notifications, setNotifications] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, u, n] = await Promise.all([
        api.get("/projects"),
        api.get("/users"),
        api.get("/project-notifications").catch(() => ({ data: [] })),
      ]);
      setProjects(p.data || []);
      setUsers(u.data || []);
      setNotifications(n.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Iter 36.7 Phase 3 — cross-page refresh. Refetch whenever any user /
  // project / sidebar mutation lands, so changes made in the Team tab or
  // sidebar Manage-Team dialog show up here without a page reload.
  useDataBus(load, ["users", "projects", "any"]);
  // Iter 36.7 Phase 4 — cross-USER live sync. The dataBus above only covers
  // mutations made inside THIS browser tab. To surface changes made by other
  // users (e.g. Content TL adds a CM → Publishing TL viewing the same
  // project must see it), poll every 20s and also refetch on window focus.
  useEffect(() => {
    const iv = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const isAdmin = (user?.role === "admin" || user?.role === "super_admin");
  const isCM = user?.role === "content_manager";
  const isTL = user?.role === "team_lead";
  // Publishing TLs get a *publisher*-missing banner instead of writer-missing
  // (per user directive). Content TLs (or legacy TLs without a tl_kind) keep
  // the classic writer-missing banner.
  const isPubTL = isTL && (user?.tl_kind === "publishing");
  const isContentTL = isTL && !isPubTL;
  const canEditProject = (p) => {
    if (isAdmin) return true;
    if (isCM) return (p.content_manager_ids || []).includes(user.id);
    if (isTL) return (p.team_lead_ids || []).includes(user.id);
    return false;
  };
  const canEditTLsOn = (p) => canEditProject(p) && (isAdmin || isCM);
  const canEditCMsOn = (p) => canEditProject(p) && isAdmin;
  const canDelete = (p) => canEditProject(p) && (isAdmin || isCM);

  // Broadened pickers (iter 35.14): every project slot picker now shows the
  // full visible-user list. Hierarchy is enforced at the API (`/api/users`
  // returns only in-scope users per `get_visible_user_ids`), so a CM sees
  // their tree, a TL sees their team+associates, admins see everyone.
  //   • `teamLeadPool` still filters by role for the Content-TL / Publishing-TL
  //     chip columns (data disambiguation — you shouldn't pick a Writer to
  //     be a team's Content TL).
  //   • CM / Writer / Publisher / Editor slots use the FULL visible list so
  //     the same person can hold multiple positions across projects.
  // Iter 36.7 Phase 2.6 — SCOPED picker pool. The user's ask:
  //   • CM  → all their team members (TLs + associates under them) + self
  //   • TL  → all their team members (associates under them + their CM) + self
  //   • Admin / Super Admin / Department Admin → full visible list
  //
  // The backend already returns only the RBAC-visible users via
  // `get_visible_user_ids`, so for CMs and TLs the returned `users` list
  // *is* their pool. Admins see everyone. Nothing more to filter here —
  // the list is already properly scoped. Any user in `users` is pickable
  // as any role, and role tags disambiguate.
  const teamLeadPool = users;
  const cmPool = users;
  const associatePool = users;

  // Iter 36.7 Phase 3 — draggable column order for the Projects table.
  // Project (first) and the actions cell (last) are anchors; the 9 middle
  // columns can be dragged left/right and the order is persisted per-user
  // in localStorage so it survives refreshes on the same browser.
  const DEFAULT_COL_ORDER = ["status", "status_note", "cm", "content_tl", "publishing_tl", "writers", "publishers", "editors"];
  const LS_COL_KEY = `pluto.projectsColOrder.${user?.id || "anon"}`;
  const [colOrder, setColOrder] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_COL_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const known = arr.filter((k) => DEFAULT_COL_ORDER.includes(k));
          const missing = DEFAULT_COL_ORDER.filter((k) => !known.includes(k));
          return [...known, ...missing];
        }
      }
    } catch (e) { /* corrupt localStorage — fall through */ }
    return DEFAULT_COL_ORDER;
  });
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const persistColOrder = (next) => {
    setColOrder(next);
    try { localStorage.setItem(LS_COL_KEY, JSON.stringify(next)); } catch (e) { /* quota — ignore */ }
  };
  const onColDragStart = (key) => (e) => {
    setDragKey(key);
    try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", key); } catch {}
  };
  const onColDragOver = (key) => (e) => { e.preventDefault(); if (dragKey && dragKey !== key) setDragOverKey(key); };
  const onColDrop = (targetKey) => (e) => {
    e.preventDefault();
    if (!dragKey || dragKey === targetKey) { setDragKey(null); setDragOverKey(null); return; }
    const next = colOrder.filter((k) => k !== dragKey);
    const idx = next.indexOf(targetKey);
    next.splice(idx < 0 ? next.length : idx, 0, dragKey);
    persistColOrder(next);
    setDragKey(null);
    setDragOverKey(null);
  };
  const onColDragEnd = () => { setDragKey(null); setDragOverKey(null); };

  const patchInline = async (proj, patch) => {
    const prev = { ...proj };
    setProjects((all) => all.map((x) => (x.id === proj.id ? { ...x, ...patch } : x)));
    try {
      await api.patch(`/projects/${proj.id}`, patch);
    } catch (e) {
      toast.error(formatApiError(e));
      setProjects((all) => all.map((x) => (x.id === prev.id ? prev : x)));
    }
  };

  // Content TL / Publishing TL columns write into `team_lead_ids` — the union
  // of both kinds. `nextKindIds` are the new selections for the picker's kind;
  // `preservedIds` are the OTHER kind's IDs that must not be removed.
  const onPatchTLs = (proj, nextKindIds, preservedIds) => {
    const merged = Array.from(new Set([...(preservedIds || []), ...(nextKindIds || [])]));
    return patchInline(proj, { team_lead_ids: merged });
  };

  // Iter 36.7 Phase 3 — column renderers indexed by column key. `label` is
  // the header text; `cell(p)` returns the <td> body for a given project row.
  // These are defined inside the component so they close over `userMap`,
  // `patchInline`, `canEdit*`, etc.
  const COL_DEFS = {
    status:        { label: "Status",        cell: (p) => <InlineStatusPill project={p} canEdit={canEditProject(p)} onPatch={patchInline} /> },
    status_note:   { label: "Status note",   cell: (p) => <InlineStatusNote project={p} canEdit={canEditProject(p)} onPatch={patchInline} /> },
    cm:            { label: "CM",            cell: (p) => <InlineAssignees project={p} field="content_manager_ids" ids={p.content_manager_ids} userMap={userMap} users={cmPool} colorClass="bg-purple-100 text-purple-700" label="Content Managers" canEdit={canEditCMsOn(p)} testId={`chip-cm-${p.id}`} onPatch={patchInline} /> },
    content_tl:    { label: "Content TL",    cell: (p) => {
      // Show every user except actual Publishing-kind TLs (those belong in
      // the Publishing TL column). Self is always allowed regardless.
      const contentTLPool = teamLeadPool.filter((u) => !(u.role === "team_lead" && u.tl_kind === "publishing") || u.id === user?.id);
      const currentIds = (p.team_lead_ids || []).filter((tid) => {
        const u = userMap[tid];
        return u && u.tl_kind !== "publishing";
      });
      const otherIds = (p.team_lead_ids || []).filter((tid) => {
        const u = userMap[tid];
        return u && u.tl_kind === "publishing";
      });
      return (
        <InlineAssignees
          project={p}
          field="__content_tls"
          ids={currentIds}
          userMap={userMap}
          users={contentTLPool}
          colorClass="bg-emerald-100 text-emerald-700"
          label="Content TLs"
          canEdit={canEditTLsOn(p)}
          testId={`chip-ctl-${p.id}`}
          onPatch={(_proj, patch) => onPatchTLs(p, patch.__content_tls, otherIds)}
        />
      );
    } },
    publishing_tl: { label: "Publishing TL", cell: (p) => {
      // Show every user except actual Content-kind TLs (those belong in
      // the Content TL column). Self is always allowed regardless.
      const publishingTLPool = teamLeadPool.filter((u) => !(u.role === "team_lead" && u.tl_kind !== "publishing") || u.id === user?.id);
      const currentIds = (p.team_lead_ids || []).filter((tid) => {
        const u = userMap[tid];
        return u && u.tl_kind === "publishing";
      });
      const otherIds = (p.team_lead_ids || []).filter((tid) => {
        const u = userMap[tid];
        return u && u.tl_kind !== "publishing";
      });
      return (
        <InlineAssignees
          project={p}
          field="__publishing_tls"
          ids={currentIds}
          userMap={userMap}
          users={publishingTLPool}
          colorClass="bg-purple-100 text-purple-700"
          label="Publishing TLs"
          canEdit={canEditTLsOn(p)}
          testId={`chip-ptl-${p.id}`}
          onPatch={(_proj, patch) => onPatchTLs(p, patch.__publishing_tls, otherIds)}
        />
      );
    } },
    writers:       { label: "Writers",       cell: (p) => <InlineAssignees project={p} field="writer_ids" ids={p.writer_ids} userMap={userMap} users={associatePool} colorClass="bg-emerald-100 text-emerald-700" label="Writers" canEdit={canEditProject(p)} testId={`chip-w-${p.id}`} onPatch={patchInline} /> },
    publishers:    { label: "Publishers",    cell: (p) => <InlineAssignees project={p} field="publisher_ids" ids={p.publisher_ids} userMap={userMap} users={associatePool} colorClass="bg-amber-100 text-amber-700" label="Publishers" canEdit={canEditProject(p)} testId={`chip-p-${p.id}`} onPatch={patchInline} /> },
    editors:       { label: "Editors",       cell: (p) => <InlineAssignees project={p} field="editor_ids" ids={p.editor_ids} userMap={userMap} users={associatePool} colorClass="bg-pink-100 text-pink-700" label="Editors" canEdit={canEditProject(p)} testId={`chip-e-${p.id}`} onPatch={patchInline} /> },
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      toast.success("Project deleted");
      setProjects((all) => all.filter((p) => p.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDeleting(false); }
  };

  const requestKeyword = async (p) => {
    try {
      const r = await api.post("/keyword-requests", { project_id: p.id });
      setProjects((all) => all.map((x) => (x.id === p.id ? { ...x, kw_requested: true } : x)));
      toast.success(r.data?.duplicate ? "Already requested" : "Keyword request sent to KP team");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 45.35 — bulk request keywords for every visible project that
  // currently has no keywords assigned and no pending request. Skips
  // rows already assigned/requested; reports a summary toast.
  const bulkRequestKeywords = async () => {
    const targets = filtered.filter((p) => !p.kw_assigned && !p.kw_requested && p.kw_unassigned);
    if (targets.length === 0) { toast.info("No eligible projects — every visible project already has keywords or a pending request."); return; }
    let ok = 0, dup = 0, fail = 0;
    for (const p of targets) {
      try {
        const r = await api.post("/keyword-requests", { project_id: p.id });
        if (r.data?.duplicate) dup++; else ok++;
        setProjects((all) => all.map((x) => (x.id === p.id ? { ...x, kw_requested: true } : x)));
      } catch { fail++; }
    }
    const parts = [];
    if (ok) parts.push(`${ok} requested`);
    if (dup) parts.push(`${dup} already pending`);
    if (fail) parts.push(`${fail} failed`);
    (fail ? toast.error : toast.success)(parts.join(" · "));
  };

  const revokeKeyword = async (p) => {
    try {
      await api.delete(`/keyword-requests?project_id=${encodeURIComponent(p.id)}`);
      setProjects((all) => all.map((x) => (x.id === p.id ? { ...x, kw_requested: false } : x)));
      toast.success("Keyword request revoked");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Iter 36.9.13 — Bug 12. Banners should highlight ONLY active projects.
  // On Hold / Stopped projects are intentionally paused, so an "assign a
  // publisher" nag would be noise. Filter both banners on status.
  const ACTIVE = (p) => !["on_hold", "stopped"].includes(p.status || "to_start");

  const unassignedList = useMemo(() => projects.filter((p) => ACTIVE(p) && isUnassigned(p)), [projects]);

  // AI-ready banner (iter 35.2): Content projects with traffic >= 1500 (from
  // seo_performance) that haven't been handed off to the AI team yet.
  // Independent of the Marketing threshold — a project can be visible in both
  // banners simultaneously.
  const AI_READY_TRAFFIC = 1500;
  const aiReadyList = useMemo(() => projects.filter((p) => {
    const traffic = Number((p.seo_performance || {}).traffic || 0);
    return (p.department || "content") === "content"
      && traffic >= AI_READY_TRAFFIC
      && !p.pushed_to_ai
      // Iter 42.3 — respect the per-project Ignore flag so dismissed
      // projects stay out of the banner (they still appear in the main
      // table with a small "AI Ignored" chip that lets the owner undo).
      && !p.ai_ignored;
  }), [projects]);
  const ignoreForAi = async (proj) => {
    // Optimistic — hide row immediately, roll back on error.
    setProjects((all) => all.map((x) => (x.id === proj.id ? { ...x, ai_ignored: true } : x)));
    try { await api.post(`/projects/${proj.id}/ai-ignore`); toast.success(`"${proj.name}" ignored`); }
    catch (e) { toast.error(formatApiError(e)); setProjects((all) => all.map((x) => (x.id === proj.id ? { ...x, ai_ignored: false } : x))); }
  };
  const unignoreForAi = async (proj) => {
    setProjects((all) => all.map((x) => (x.id === proj.id ? { ...x, ai_ignored: false } : x)));
    try { await api.post(`/projects/${proj.id}/ai-unignore`); toast.success(`"${proj.name}" restored`); }
    catch (e) { toast.error(formatApiError(e)); setProjects((all) => all.map((x) => (x.id === proj.id ? { ...x, ai_ignored: true } : x))); }
  };
  const canPushToAI = user && ["content_manager", "admin", "super_admin"].includes(user.role);
  const [pushToAiOpen, setPushToAiOpen] = useState(false);
  const [pushToAiTargets, setPushToAiTargets] = useState([]); // projects to push
  // Per-project selection inside the banner — starts empty (no default pick)
  // so the CM always explicitly opts-in. Reset whenever the ready list mutates.
  const [aiSelectedIds, setAiSelectedIds] = useState([]);
  useEffect(() => {
    // Trim selection down to still-visible ready projects on data change.
    setAiSelectedIds((prev) => prev.filter((id) => aiReadyList.some((p) => p.id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiReadyList.map((p) => p.id).join(",")]);
  const aiAllSelected = aiReadyList.length > 0 && aiSelectedIds.length === aiReadyList.length;
  const toggleAiOne = (id) => setAiSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAiAll = () => setAiSelectedIds(aiAllSelected ? [] : aiReadyList.map((p) => p.id));

  // KW-unassigned list (Content projects with no keywords, not already resolved)
  const kwUnassignedList = useMemo(
    () => projects.filter((p) => ACTIVE(p) && p.kw_unassigned && !p.kw_assigned),
    [projects],
  );

  // TL-only alert: projects assigned to me that have no writers.
  // Content-kind TLs see writer-missing; Publishing-kind TLs get their own
  // publisher-missing banner below.
  const missingWriterList = useMemo(() => {
    if (!isContentTL) return [];
    return projects.filter(
      (p) => ACTIVE(p) && (p.team_lead_ids || []).includes(user?.id) && !(p.writer_ids && p.writer_ids.length > 0),
    );
  }, [projects, isContentTL, user]);

  // Publishing-TL–only alert: projects assigned to me that have no publishers.
  const missingPublisherList = useMemo(() => {
    if (!isPubTL) return [];
    return projects.filter(
      (p) => ACTIVE(p) && (p.team_lead_ids || []).includes(user?.id) && !(p.publisher_ids && p.publisher_ids.length > 0),
    );
  }, [projects, isPubTL, user]);

  // Options for the people filters: only users who appear in at least one
  // project in the corresponding role (keeps the dropdown small & relevant).
  const filterOptionsByRole = useMemo(() => {
    const roleFields = {
      cm: "content_manager_ids",
      tl: "team_lead_ids",
      writer: "writer_ids",
      editor: "editor_ids",
      publisher: "publisher_ids",
    };
    const out = { cm: new Set(), tl: new Set(), writer: new Set(), editor: new Set(), publisher: new Set() };
    for (const p of projects) {
      for (const [k, field] of Object.entries(roleFields)) {
        for (const uid of (p[field] || [])) out[k].add(uid);
      }
    }
    const toList = (s) => Array.from(s).map((id) => userMap[id]).filter(Boolean).sort((a, b) => a.name.localeCompare(b.name));
    return { cm: toList(out.cm), tl: toList(out.tl), writer: toList(out.writer), editor: toList(out.editor), publisher: toList(out.publisher) };
  }, [projects, userMap]);

  const STATUS_META_LABELS = useMemo(() => {
    const map = {};
    for (const s of PROJECT_STATUSES) map[s.value] = s.label;
    return map;
  }, []);

  const filtered = useMemo(() => {
    let list = showOnlyUnassigned ? unassignedList : projects;
    if (statusFilter !== "all") list = list.filter((p) => (p.status || "to_start") === statusFilter);
    // People filters — narrow to projects containing the selected user in
    // the corresponding role. Multiple filters AND together.
    if (cmFilter !== "any") list = list.filter((p) => (p.content_manager_ids || []).includes(cmFilter));
    if (tlFilter !== "any") list = list.filter((p) => (p.team_lead_ids || []).includes(tlFilter));
    if (writerFilter !== "any") list = list.filter((p) => (p.writer_ids || []).includes(writerFilter));
    if (editorFilter !== "any") list = list.filter((p) => (p.editor_ids || []).includes(editorFilter));
    if (publisherFilter !== "any") list = list.filter((p) => (p.publisher_ids || []).includes(publisherFilter));
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        if ((p.name || "").toLowerCase().includes(q)) return true;
        if ((p.site_url || "").toLowerCase().includes(q)) return true;
        if ((p.status_note || "").toLowerCase().includes(q)) return true;
        const statusLabel = STATUS_META_LABELS[p.status || "to_start"];
        if (statusLabel && statusLabel.toLowerCase().includes(q)) return true;
        const allIds = [
          ...(p.team_lead_ids || []),
          ...(p.content_manager_ids || []),
          ...(p.writer_ids || []),
          ...(p.publisher_ids || []),
          ...(p.editor_ids || []),
        ];
        for (const id of allIds) {
          const u = userMap[id];
          if (u && u.name.toLowerCase().includes(q)) return true;
          if (u && (u.email || "").toLowerCase().includes(q)) return true;
        }
        return false;
      });
    }
    return list;
  }, [projects, unassignedList, showOnlyUnassigned, statusFilter, search, userMap, cmFilter, tlFilter, writerFilter, editorFilter, publisherFilter, STATUS_META_LABELS]);

  const anyPeopleFilterActive = cmFilter !== "any" || tlFilter !== "any" || writerFilter !== "any" || editorFilter !== "any" || publisherFilter !== "any";
  const clearPeopleFilters = () => {
    setCmFilter("any"); setTlFilter("any"); setWriterFilter("any"); setEditorFilter("any"); setPublisherFilter("any");
  };

  // Iter 44.5 — status card counts for the clickable strip above the
  // quick-filters. Mirrors the Dashboard's Task Status Breakdown pattern:
  // click a card → set the status filter to that value; clicking the
  // active one again clears back to "all".
  const statusCounts = useMemo(() => {
    const counts = { all: projects.length };
    for (const s of PROJECT_STATUSES) counts[s.value] = 0;
    for (const p of projects) {
      const st = p.status || "to_start";
      if (counts[st] !== undefined) counts[st] += 1;
    }
    return counts;
  }, [projects]);

  // Iter 44 — project-notification handlers. Ignore is per-user only; the
  // NewChangesBanner component owns the visuals + action chips + relative
  // time. Optimistic update so the banner shrinks instantly on click.
  const ackOne = async (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try { await api.post(`/project-notifications/${id}/ack`); } catch { /* silent — banner already hidden */ }
  };
  const ackAll = async () => {
    setNotifications([]);
    try { await api.post(`/project-notifications/ack-all`); } catch { /* silent */ }
  };

  // Iter 45.39 — six sortable pill columns fed by task_status_counts +
  // urgent_count from GET /api/projects. Fixed at the end of the table
  // (not part of the draggable colOrder). Click a header to sort:
  // none → desc → asc → none.
  const STATUS_COLS = [
    { key: "urgent",     label: "Urgent",             get: (p) => p.urgent_count || 0,                              pillCls: "bg-red-50 text-red-700 border-red-200" },
    { key: "todo",       label: "To-Do",              get: (p) => (p.task_status_counts || {})["To-Do"] || 0,              pillCls: "bg-slate-100 text-slate-700 border-slate-300" },
    { key: "editing",    label: "Editing Pending",    get: (p) => (p.task_status_counts || {})["Editing Pending"] || 0,    pillCls: "bg-blue-50 text-blue-700 border-blue-200" },
    { key: "publishing", label: "Publishing Pending", get: (p) => (p.task_status_counts || {})["Publishing Pending"] || 0, pillCls: "bg-amber-50 text-amber-700 border-amber-200" },
    { key: "review",     label: "Review Pending",     get: (p) => (p.task_status_counts || {})["Review Pending"] || 0,     pillCls: "bg-purple-50 text-purple-700 border-purple-200" },
    { key: "published",  label: "Published",          get: (p) => (p.task_status_counts || {})["Published"] || 0,          pillCls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ];
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("desc"); }
    else if (sortDir === "desc") setSortDir("asc");
    else { setSortKey(null); setSortDir("desc"); }
  };
  const sortedFiltered = useMemo(() => {
    if (!sortKey) return filtered;
    const col = STATUS_COLS.find((c) => c.key === sortKey);
    if (!col) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => (col.get(a) - col.get(b)) * dir);
    // eslint-disable-next-line
  }, [filtered, sortKey, sortDir]);

  return (
    <div className="w-full max-w-[1400px] min-w-0 mx-auto p-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Projects</h1>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200" data-testid="projects-count-chip">
              {loading ? "…" : projects.length}
            </span>
          </div>
          <p className="text-sm text-slate-500 font-medium mt-0.5">{loading ? "Loading…" : `${projects.length} project${projects.length === 1 ? "" : "s"} accessible to you.`}</p>
        </div>
        {/* Add Project — visible to any role the backend allows to POST /projects
            (admin / super_admin / department_admin / content_manager / team_lead).
            Opens an inline dialog (matches the Projects tab columns) instead of
            navigating away — keeps filters and scroll position intact. */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Iter 45.35 — Bulk "Request Keywords" toolbar button. Counts
              eligible projects (no keywords assigned, no pending request)
              and fires a keyword-request for each. Same permission gate
              as the per-row KW button. */}
          {user && (["admin", "super_admin", "content_manager", "team_lead"].includes(user.role) ||
            (user.role === "department_admin" && (user.departments || []).includes("content"))) && (() => {
              const eligible = filtered.filter((p) => !p.kw_assigned && !p.kw_requested && p.kw_unassigned).length;
              return (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={bulkRequestKeywords}
                  disabled={eligible === 0}
                  className="border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 hover:text-fuchsia-800 hover:border-fuchsia-300 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  title={eligible === 0 ? "No eligible projects — every visible project already has keywords or a pending request." : `Request keywords for ${eligible} project${eligible === 1 ? "" : "s"} without them.`}
                  data-testid="projects-bulk-request-kw-btn"
                >
                  <Sparkles className="h-4 w-4 mr-1.5" /> Request Keywords
                  {eligible > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[18px] px-1 rounded-full bg-fuchsia-100 text-fuchsia-700 text-[10px] font-bold" data-testid="projects-bulk-request-kw-count">{eligible}</span>
                  )}
                </Button>
              );
            })()}
          {user && ["admin", "super_admin", "department_admin", "content_manager", "team_lead"].includes(user.role) && (
            <Button
              size="sm"
              onClick={() => setShowCreate(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white shadow-sm hover:shadow-md hover:shadow-purple-500/20 transition"
              data-testid="projects-add-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Project
            </Button>
          )}
        </div>
      </div>

      {/* Iter 42.3 — Team hierarchy overview. Component decides visibility
          internally (admin / super-admin / content-dept DA see everyone;
          CM sees just their own tree; other roles get null). */}
      <TeamHierarchyCard user={user} projects={projects} users={users} />

      {/* Iter 44 — "New Changes" banner: matches AI-banner style. Only
          visible to Admin / Super-Admin / Content Dept-Admin (backend
          gates that; associates/CMs/TLs get [] from /project-notifications).
          Same banner also renders on the Dashboard home. */}
      <NewChangesBanner
        notifications={notifications}
        onIgnore={(n) => ackOne(n.id)}
        onIgnoreAll={ackAll}
      />

      {aiReadyList.length > 0 && canPushToAI && (
        <AiReadyBanner
          aiReadyList={aiReadyList}
          aiReadyTraffic={AI_READY_TRAFFIC}
          aiSelectedIds={aiSelectedIds}
          aiAllSelected={aiAllSelected}
          toggleAiAll={toggleAiAll}
          toggleAiOne={toggleAiOne}
          onPush={() => {
            const targets = aiReadyList.filter((p) => aiSelectedIds.includes(p.id));
            if (targets.length === 0) { toast.error("Select at least one project"); return; }
            setPushToAiTargets(targets);
            setPushToAiOpen(true);
          }}
          onIgnore={ignoreForAi}
        />
      )}

      {unassignedList.length > 0 && (
        <UnassignedBanner
          unassignedList={unassignedList}
          open={showUnassignedDropdown}
          setOpen={setShowUnassignedDropdown}
          onAssign={setEditTarget}
        />
      )}

      {isContentTL && missingWriterList.length > 0 && (
        <MissingWriterBanner
          missingWriterList={missingWriterList}
          open={showMissingWriterDropdown}
          setOpen={setShowMissingWriterDropdown}
          onAssign={setEditTarget}
        />
      )}

      {isPubTL && missingPublisherList.length > 0 && (
        <MissingPublisherBanner
          missingPublisherList={missingPublisherList}
          open={showMissingPublisherDropdown}
          setOpen={setShowMissingPublisherDropdown}
          onAssign={setEditTarget}
        />
      )}

      {kwUnassignedList.length > 0 && (
        <KwUnassignedBanner
          kwUnassignedList={kwUnassignedList}
          onRequest={requestKeyword}
          onRevoke={revokeKeyword}
        />
      )}

      {/* Iter 44.5 — clickable status cards above the quick filters.
          Mirrors the Dashboard's Task Status Breakdown card grid. Click
          a card to filter, click the active one to clear. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="projects-status-strip">
        {[{ value: "all", label: "All", dot: "bg-slate-400", chip: "" }, ...PROJECT_STATUSES].map((s) => {
          const active = statusFilter === s.value;
          const count = statusCounts[s.value] ?? 0;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setStatusFilter(active && s.value !== "all" ? "all" : s.value)}
              className={`p-3 rounded-xl border text-left transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md ${
                active
                  ? "border-purple-400 bg-purple-50 ring-2 ring-purple-200 shadow-sm hover:shadow-purple-500/10"
                  : "border-slate-200/80 bg-white shadow-sm hover:border-purple-200 hover:bg-purple-50/30"
              }`}
              data-testid={`projects-status-card-${s.value}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[11px] uppercase tracking-wider font-semibold ${active ? "text-purple-700" : "text-slate-500"}`}>
                  {s.label}
                </span>
                <span className={`h-2 w-2 rounded-full ${s.dot}`} />
              </div>
              <div className={`mt-2 font-display text-2xl font-bold leading-none ${active ? "text-purple-900" : "text-slate-900"}`}>
                {count}
              </div>
            </button>
          );
        })}
      </div>

      <ProjectFilters
        search={search} setSearch={setSearch}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        cmFilter={cmFilter} setCmFilter={setCmFilter}
        tlFilter={tlFilter} setTlFilter={setTlFilter}
        writerFilter={writerFilter} setWriterFilter={setWriterFilter}
        editorFilter={editorFilter} setEditorFilter={setEditorFilter}
        publisherFilter={publisherFilter} setPublisherFilter={setPublisherFilter}
        filterOptionsByRole={filterOptionsByRole}
        userMap={userMap}
        anyPeopleFilterActive={anyPeopleFilterActive}
        clearPeopleFilters={clearPeopleFilters}
        showOnlyUnassigned={showOnlyUnassigned} setShowOnlyUnassigned={setShowOnlyUnassigned}
        filteredCount={filtered.length} totalCount={projects.length}
      />

      {/* Projects table — Iter 45.31 redesign + Iter 45.35 sticky header
          + progress column. Sticky ths pin to the AppLayout column
          scroller as the user scrolls the whole page down. The inner
          wrapper deliberately has NO overflow-x-auto — that would create
          a scroll containing block and scope sticky to itself (not the
          outer scroller), breaking cross-boundary stickying. AppLayout's
          column already handles horizontal scroll on narrow viewports. */}
      {/* Iter 45.40 — the table scrolls INSIDE its own card window
          (overflow-auto + max-h) so the wide column set never pushes the
          banners/toolbar sideways. This scroller is also the sticky
          header's scrollport, so the frozen header keeps working while
          scrolling the table body vertically. */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-auto max-h-[calc(100vh-280px)]">
          <table className="w-full min-w-max text-sm">
            <thead className="bg-slate-50 border-b border-slate-200/80">
              <tr>
                <th className="sticky top-0 z-10 bg-slate-50 text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Project</th>
                {colOrder.map((key) => {
                  const def = COL_DEFS[key];
                  if (!def) return null;
                  const isOver = dragOverKey === key && dragKey && dragKey !== key;
                  const isDragging = dragKey === key;
                  return (
                    <th
                      key={key}
                      draggable
                      onDragStart={onColDragStart(key)}
                      onDragOver={onColDragOver(key)}
                      onDrop={onColDrop(key)}
                      onDragEnd={onColDragEnd}
                      onDragLeave={() => dragOverKey === key && setDragOverKey(null)}
                      className={`sticky top-0 z-10 bg-slate-50 text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 cursor-move select-none transition whitespace-nowrap ${isOver ? "bg-purple-100 ring-2 ring-purple-400 ring-inset" : ""} ${isDragging ? "opacity-40" : ""}`}
                      title="Drag to reorder column"
                      data-testid={`projects-col-header-${key}`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <GripVertical className="h-3 w-3 opacity-40 shrink-0" />
                        {def.label}
                      </span>
                    </th>
                  );
                })}
                {/* Iter 45.39 — six sortable status-count columns */}
                {STATUS_COLS.map((c) => (
                  <th
                    key={c.key}
                    className="sticky top-0 z-10 bg-slate-50 text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap"
                    data-testid={`projects-col-header-${c.key}`}
                  >
                    <button
                      onClick={() => toggleSort(c.key)}
                      className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold transition ${sortKey === c.key ? "text-purple-700" : "text-slate-500 hover:text-purple-600"}`}
                      title={`Sort by ${c.label} count`}
                      data-testid={`projects-sort-${c.key}`}
                      data-sort-state={sortKey === c.key ? sortDir : "off"}
                    >
                      {c.label}
                      {sortKey === c.key && sortDir === "desc" && <ArrowDown className="h-3 w-3" />}
                      {sortKey === c.key && sortDir === "asc" && <ArrowUp className="h-3 w-3" />}
                      {sortKey !== c.key && <ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                  </th>
                ))}
                <th className="sticky top-0 z-10 bg-slate-50 w-20 px-2 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && filtered.length === 0 && (
                <TableRowsSkeleton cols={colOrder.length + 8} rows={5} firstAvatar />
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={colOrder.length + 8} className="text-center py-10 text-sm text-slate-500">No projects match your filters.</td></tr>
              )}
              {sortedFiltered.map((p) => {
                const unassigned = isUnassigned(p);
                return (
                  <tr key={p.id} className={`hover:bg-purple-50/30 transition-colors duration-150 group ${unassigned ? "bg-red-50/50" : ""}`} data-testid={`project-row-${p.id}`}>
                    <td className="px-4 py-3 align-top min-w-[240px]">
                      <div className="flex items-start gap-2.5">
                        <span className={`h-8 w-8 shrink-0 rounded-lg border flex items-center justify-center ${unassigned ? "bg-red-50 border-red-100 text-red-500" : "bg-purple-50 border-purple-100 text-purple-600"}`}>
                          <FolderOpen className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <Link to={`/projects/${p.id}`} className="text-sm font-semibold text-slate-900 hover:text-purple-600 transition-colors truncate block" data-testid={`project-link-${p.id}`}>{p.name}</Link>
                          {p.site_url && (
                            <a href={p.site_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-purple-600 truncate flex items-center gap-1 mt-0.5 transition-colors">
                              <ExternalLink className="h-3 w-3" /> {p.site_url}
                            </a>
                          )}
                          {unassigned && <span className="mt-1 inline-block text-[9px] uppercase tracking-wider bg-red-500 text-white px-1.5 py-0.5 rounded font-bold">Unassigned</span>}
                          {p.kw_assigned && <span className="mt-1 ml-1 inline-block text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-bold" data-testid={`kw-assigned-tag-${p.id}`}>KW Assigned</span>}
                          {!p.kw_assigned && p.kw_requested && <span className="mt-1 ml-1 inline-block text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-bold" data-testid={`kw-requested-tag-${p.id}`}>KW Requested</span>}
                          {!p.kw_assigned && !p.kw_requested && p.kw_unassigned && <span className="mt-1 ml-1 inline-block text-[9px] uppercase tracking-wider bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded font-bold" data-testid={`kw-unassigned-${p.id}`}>KW Unassigned</span>}
                          {p.ai_ignored && canPushToAI && (
                            <button
                              onClick={() => unignoreForAi(p)}
                              className="mt-1 ml-1 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 hover:text-slate-800 px-1.5 py-0.5 rounded font-bold transition"
                              title="Click to restore in AI-ready banner"
                              data-testid={`ai-ignored-tag-${p.id}`}
                            >
                              AI Ignored · undo
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    {colOrder.map((key) => {
                      const def = COL_DEFS[key];
                      if (!def) return null;
                      return (
                        <td key={key} className="px-4 py-3 align-top" data-testid={`projects-cell-${key}-${p.id}`}>
                          {def.cell(p)}
                        </td>
                      );
                    })}
                    {/* Iter 45.39 — status-count pills. 0-counts render
                        dimmed so non-zero values pop at a glance. */}
                    {STATUS_COLS.map((c) => {
                      const n = c.get(p);
                      return (
                        <td key={c.key} className="px-4 py-3 align-middle whitespace-nowrap" data-testid={`projects-cell-${c.key}-${p.id}`}>
                          <span
                            className={`inline-flex items-center justify-center min-w-[26px] px-1.5 py-0.5 rounded-md border text-[11px] font-bold ${c.pillCls} ${n === 0 ? "opacity-35" : ""}`}
                            title={`${n} ${c.label}`}
                            data-testid={`projects-pill-${c.key}-${p.id}`}
                          >
                            {n}
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-2 py-3 text-right align-top">
                      <div className="flex items-center justify-end gap-0.5">
                        {((user?.role === "admin" || user?.role === "super_admin") || user?.role === "content_manager" || user?.role === "team_lead" || (user?.role === "department_admin" && (user?.departments || []).includes("content"))) && !p.kw_assigned && (
                          p.kw_requested ? (
                            <button
                              onClick={() => revokeKeyword(p)}
                              className="p-1.5 rounded text-amber-700 hover:text-white hover:bg-amber-600 transition text-[10px] font-semibold uppercase tracking-wider border border-amber-200 hover:border-amber-600"
                              title="Revoke keyword request"
                              data-testid={`revoke-kw-btn-${p.id}`}
                            >
                              ✕KW
                            </button>
                          ) : (
                            <button
                              onClick={() => requestKeyword(p)}
                              className="p-1.5 rounded text-fuchsia-600 hover:text-white hover:bg-fuchsia-600 transition text-[10px] font-semibold uppercase tracking-wider border border-fuchsia-200 hover:border-fuchsia-600"
                              title="Request keywords from Keyword Planning team"
                              data-testid={`request-kw-${p.id}`}
                            >
                              KW
                            </button>
                          )
                        )}
                        <button onClick={() => setEditTarget(p)} className="p-1.5 rounded text-slate-500 hover:text-purple-600 hover:bg-purple-50 transition" title="Edit project" data-testid={`project-edit-${p.id}`}>
                          <Settings2 className="h-4 w-4" />
                        </button>
                        {canDelete(p) && (
                          <button onClick={() => setDeleteTarget(p)} className="p-1.5 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 transition" title="Delete project" data-testid={`project-delete-${p.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
      </div>

      <EditProjectDialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        project={editTarget}
        users={users}
        currentUser={user}
        onSaved={load}
      />

      <EditProjectDialog
        mode="create"
        open={showCreate}
        onClose={() => setShowCreate(false)}
        project={null}
        users={users}
        currentUser={user}
        onSaved={load}
      />

      <PushToAIDialog
        open={pushToAiOpen}
        onClose={() => setPushToAiOpen(false)}
        projects={pushToAiTargets}
        onDone={() => { setPushToAiOpen(false); load(); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-project-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700"><Trash2 className="h-5 w-5" /> Delete project?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete <strong>{deleteTarget?.name}</strong>. All tasks, keywords, and per-project data will be removed. <span className="font-semibold text-red-600">This cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-project-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="delete-project-confirm">
              {deleting ? "Deleting…" : "Yes, delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
